import { Fragment, useCallback, useEffect, useRef, useState } from "react";
import { Link as RouterLink, useSearchParams } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  Link,
  MenuItem,
  Paper,
  Stack,
  Switch,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import {
  Aperture,
  ArrowRight,
  Buildings,
  ChartLineUp,
  ClipboardText,
  Copy,
  HardDrives,
  Planet,
  Plus,
  Pulse,
  RocketLaunch,
  TerminalWindow,
  UsersThree,
} from "@phosphor-icons/react";
import {
  AuditRow,
  CHANNELS,
  DicomNode,
  AE_PATTERN,
  downloadFile,
  Edge,
  fieldErrors,
  Fleet,
  http,
  ModuleRow,
  RECORDING_QUALITIES,
  Site,
  Stats,
  Tenant,
  UserRow,
} from "../api";
import { useSession } from "../AuthToken";
import { ConnectionDots, Mono, PageHeader, SectionLabel, StatusChip } from "../ui";
import { DiagnosticPanel, DiagRun, DIAG_MAX_MS, DIAG_POLL_MS, newer } from "../console/DiagnosticPanel";
import { fonts, tones } from "../theme";

const ago = (iso: string | null) => {
  if (!iso) return "never";
  const s = Math.round((Date.now() - new Date(iso).getTime()) / 1000);
  return s < 90
    ? `${s}s ago`
    : s < 5400
    ? `${Math.round(s / 60)}m ago`
    : s < 129600
    ? `${Math.round(s / 3600)}h ago`
    : `${Math.round(s / 86400)}d ago`;
};

const TABS = ["overview", "sites", "edges", "instruments", "users", "audit", "fleet"];

export default function Admin() {
  // The tab lives in the URL (?tab=edges) so the detail pages can link back to the right one.
  const [params, setParams] = useSearchParams();
  const tab = Math.max(0, TABS.indexOf(params.get("tab") ?? "overview"));
  const setTab = (i: number) => setParams(i ? { tab: TABS[i] } : {}, { replace: true });
  const { me } = useSession();
  if (me && me.role !== "admin") return <Alert severity="info">The admin console requires the admin role.</Alert>;
  return (
    <Stack spacing={3}>
      <PageHeader overline={me?.tenant?.name ?? "Console"} title="Admin" />
      <Tabs value={tab} onChange={(_, v) => setTab(v)} variant="scrollable" allowScrollButtonsMobile>
        {[
          { label: "Overview", icon: <ChartLineUp size={16} /> },
          { label: "Sites & enrollment", icon: <Buildings size={16} /> },
          { label: "Pandia", icon: <HardDrives size={16} /> },
          { label: "Instruments", icon: <Aperture size={16} /> },
          { label: "Users", icon: <UsersThree size={16} /> },
          { label: "Audit", icon: <ClipboardText size={16} /> },
          { label: "Updates", icon: <RocketLaunch size={16} /> },
        ].map((t) => (
          <Tab key={t.label} label={t.label} icon={t.icon} iconPosition="start" sx={{ gap: 0.5 }} />
        ))}
      </Tabs>
      {tab === 0 && <Overview />}
      {tab === 1 && <SitesTab />}
      {tab === 2 && <EdgesTab />}
      {tab === 3 && <InstrumentsTab />}
      {tab === 4 && <UsersTab />}
      {tab === 5 && <AuditTab />}
      {tab === 6 && <FleetTab />}
    </Stack>
  );
}

function useLoad<T>(path: string, every = 0) {
  const { token } = useSession();
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState("");
  const load = useCallback(async () => {
    if (!token) return;
    const r = await http.get<T>(token, path);
    if (r.ok) setData(r.result);
    else setError(r.error);
  }, [token, path]);
  useEffect(() => {
    load();
    if (!every) return;
    const iv = setInterval(load, every);
    return () => clearInterval(iv);
  }, [load, every]);
  return { data, error, setError, load };
}

function Stat({ label, value, hint }: { label: string; value: string | number; hint?: string }) {
  return (
    <Card variant="outlined" sx={{ height: "100%", position: "relative", overflow: "hidden" }}>
      <Box
        aria-hidden
        sx={{
          position: "absolute",
          right: -40,
          top: -60,
          width: 140,
          height: 140,
          borderRadius: "50%",
          background: `radial-gradient(circle, ${alpha(tones.ochre, 0.09)}, transparent 70%)`,
        }}
      />
      <CardContent sx={{ position: "relative" }}>
        <Typography variant="overline" color="text.secondary" sx={{ display: "block", lineHeight: 1.5 }}>
          {label}
        </Typography>
        <Typography
          sx={{
            fontFamily: fonts.display,
            fontWeight: 200,
            fontSize: { xs: 30, md: 38 },
            letterSpacing: "0.02em",
            lineHeight: 1.2,
            mt: 0.5,
          }}
        >
          {value}
        </Typography>
        {hint && (
          <Typography variant="caption" color="text.secondary">
            {hint}
          </Typography>
        )}
      </CardContent>
    </Card>
  );
}

function Overview() {
  const { data: s } = useLoad<Stats>("/admin/stats", 15_000);
  if (!s) return null;
  const count = (arr: { _count: number }[], pick: (x: any) => boolean) =>
    arr.filter(pick).reduce((a, b) => a + b._count, 0);
  return (
    <Grid container spacing={2}>
      <Grid item xs={6} md={3}>
        <Stat label="Pandia online" value={`${s.edgesOnline} / ${s.edgesTotal}`} />
      </Grid>
      <Grid item xs={6} md={3}>
        <Stat label="Instruments pending" value={s.nodesPending} hint="awaiting approval" />
      </Grid>
      <Grid item xs={6} md={3}>
        <Stat label="People served" value={s.peopleServed} hint="distinct patients with captures (grant metric)" />
      </Grid>
      <Grid item xs={6} md={3}>
        <Stat label="Archived" value={`${(s.archivedBytes / 1048576).toFixed(1)} MB`} />
      </Grid>
      <Grid item xs={6} md={3}>
        <Stat label="Orders scheduled" value={count(s.orders, (o) => o.status === "scheduled")} />
      </Grid>
      <Grid item xs={6} md={3}>
        <Stat label="Orders completed" value={count(s.orders, (o) => o.status === "completed")} />
      </Grid>
      <Grid item xs={6} md={3}>
        <Stat label="Objects in HealthImaging" value={count(s.instances, (i) => i.storage === "healthimaging")} />
      </Grid>
      <Grid item xs={6} md={3}>
        <Stat
          label="Objects in S3"
          value={count(s.instances, (i) => i.storage === "s3")}
          hint={`${s.recordings} recordings`}
        />
      </Grid>
    </Grid>
  );
}

function SitesTab() {
  const { token } = useSession();
  const { data: sites, load, error, setError } = useLoad<Site[]>("/admin/sites");
  const { data: tenants, load: loadTenants } = useLoad<Tenant[]>("/admin/tenants");
  const [site, setSite] = useState({ tenantCode: "", code: "", name: "" });
  const [tenant, setTenant] = useState({ code: "", name: "" });
  const [enroll, setEnroll] = useState<{ siteId: string; deviceName: string } | null>(null);
  const [issued, setIssued] = useState<{ code: string; command: string; expiresAt: string } | null>(null);

  const addSite = async () => {
    const r = await http.post(token, "/admin/sites", { ...site, tenantCode: site.tenantCode || tenants?.[0]?.code });
    if (!r.ok) return setError(r.error);
    setSite({ tenantCode: "", code: "", name: "" });
    load();
  };
  const addTenant = async () => {
    const r = await http.post(token, "/admin/tenants", tenant);
    if (!r.ok) return setError(r.error);
    setTenant({ code: "", name: "" });
    loadTenants();
  };
  const issue = async () => {
    if (!enroll) return;
    const r = await http.post<{ code: string; command: string; expiresAt: string }>(
      token,
      `/admin/sites/${enroll.siteId}/enrollments`,
      { deviceName: enroll.deviceName, ttlHours: 48 }
    );
    if (!r.ok) return setError(r.error);
    setEnroll(null);
    setIssued(r.result);
  };

  const setQuality = async (code: string, defaultRecordingQuality: string) => {
    const r = await http.patch(token, `/admin/tenants/${code}`, { defaultRecordingQuality });
    if (!r.ok) return setError(r.error);
    loadTenants();
  };

  return (
    <Stack spacing={2}>
      {error && (
        <Alert severity="error" onClose={() => setError("")}>
          {error}
        </Alert>
      )}
      <SectionLabel>Tenants</SectionLabel>
      <Paper sx={{ overflowX: "auto" }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Tenant</TableCell>
              <TableCell>Name</TableCell>
              <TableCell>Remote session recordings (default)</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {(tenants ?? []).map((t) => (
              <TableRow key={t.id}>
                <TableCell>
                  <Mono>{t.code}</Mono>
                </TableCell>
                <TableCell>{t.name}</TableCell>
                <TableCell>
                  <TextField
                    size="small"
                    select
                    value={t.defaultRecordingQuality}
                    onChange={(e) => setQuality(t.code, e.target.value)}
                    sx={{ minWidth: 240 }}
                  >
                    {RECORDING_QUALITIES.map((q) => (
                      <MenuItem key={q.value} value={q.value}>
                        {q.label}
                      </MenuItem>
                    ))}
                  </TextField>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>
      <EmrCard />
      <SectionLabel>Sites</SectionLabel>
      <Paper sx={{ overflowX: "auto" }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Tenant</TableCell>
              <TableCell>Site</TableCell>
              <TableCell>Name</TableCell>
              <TableCell>Pandia</TableCell>
              <TableCell>Update window</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {(sites ?? []).map((s) => (
              <TableRow key={s.id}>
                <TableCell>
                  <Mono>{s.tenant.code}</Mono>
                </TableCell>
                <TableCell>
                  <Mono>{s.code}</Mono>
                </TableCell>
                <TableCell>{s.name}</TableCell>
                <TableCell>
                  {s.devices
                    .filter((d) => !d.revokedAt)
                    .map((d) => d.name)
                    .join(", ") || "none"}
                </TableCell>
                <TableCell>
                  <SiteWindow site={s} onSaved={load} />
                </TableCell>
                <TableCell align="right">
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<Plus size={14} />}
                    onClick={() => setEnroll({ siteId: s.id, deviceName: `${s.code}-edge-1` })}
                  >
                    Add a Pandia
                  </Button>
                </TableCell>
              </TableRow>
            ))}
            {(sites ?? []).length === 0 && (
              <TableRow>
                <TableCell colSpan={6} sx={{ py: 4, textAlign: "center", color: "text.secondary" }}>
                  No sites yet.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Paper>
      <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
        <Paper sx={{ p: 2, flex: 1 }}>
          <Typography variant="overline" color="text.secondary" sx={{ display: "block", mb: 1 }}>
            New site
          </Typography>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
            <TextField
              size="small"
              select
              label="Tenant"
              value={site.tenantCode || tenants?.[0]?.code || ""}
              onChange={(e) => setSite({ ...site, tenantCode: e.target.value })}
              sx={{ minWidth: 140 }}
            >
              {(tenants ?? []).map((t) => (
                <MenuItem key={t.id} value={t.code}>
                  {t.code}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              size="small"
              label="Code"
              placeholder="carilion-giles"
              value={site.code}
              onChange={(e) => setSite({ ...site, code: e.target.value })}
            />
            <TextField
              size="small"
              label="Name"
              value={site.name}
              onChange={(e) => setSite({ ...site, name: e.target.value })}
            />
            <Button startIcon={<Plus size={14} />} onClick={addSite} disabled={!site.code || !site.name}>
              Add
            </Button>
          </Stack>
        </Paper>
        <Paper sx={{ p: 2, flex: 1 }}>
          <Typography variant="overline" color="text.secondary" sx={{ display: "block", mb: 1 }}>
            New tenant
          </Typography>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
            <TextField
              size="small"
              label="Code"
              value={tenant.code}
              onChange={(e) => setTenant({ ...tenant, code: e.target.value })}
            />
            <TextField
              size="small"
              label="Name"
              value={tenant.name}
              onChange={(e) => setTenant({ ...tenant, name: e.target.value })}
            />
            <Button startIcon={<Plus size={14} />} onClick={addTenant} disabled={!tenant.code || !tenant.name}>
              Add
            </Button>
          </Stack>
        </Paper>
      </Stack>
      <Dialog open={!!enroll} onClose={() => setEnroll(null)}>
        <DialogTitle>Add a Pandia</DialogTitle>
        <DialogContent>
          <TextField
            sx={{ mt: 1 }}
            fullWidth
            label="Appliance name"
            value={enroll?.deviceName ?? ""}
            onChange={(e) => enroll && setEnroll({ ...enroll, deviceName: e.target.value })}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button color="inherit" onClick={() => setEnroll(null)}>
            Cancel
          </Button>
          <Button variant="contained" onClick={issue}>
            Create enrollment code
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog open={!!issued} onClose={() => setIssued(null)} maxWidth="md" fullWidth>
        <DialogTitle>Run this command on the site's Pandia box</DialogTitle>
        <DialogContent>
          <Typography variant="body2" gutterBottom>
            Run one command as root on a Linux host at the site. The enrollment code can be used once and expires{" "}
            {issued && new Date(issued.expiresAt).toLocaleString()}.
          </Typography>
          <Box sx={{ position: "relative", my: 1.5 }}>
            <Box sx={{ position: "absolute", left: 14, top: 14, color: "primary.main", display: "flex" }}>
              <TerminalWindow size={16} />
            </Box>
            <Box
              component="pre"
              sx={{
                m: 0,
                p: 2,
                pl: 5,
                bgcolor: "rgba(0,0,0,0.45)",
                border: 1,
                borderColor: "divider",
                borderRadius: "12px",
                overflowX: "auto",
                fontSize: 13,
                fontFamily: fonts.mono,
                color: "#f3e4cf",
              }}
            >
              {issued?.command}
            </Box>
          </Box>
          <Typography variant="body2">
            Enrollment code: <Mono sx={{ color: "primary.light", fontSize: 15 }}>{issued?.code}</Mono>
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button
            startIcon={<Copy size={14} />}
            onClick={() => issued && navigator.clipboard.writeText(issued.command)}
          >
            Copy command
          </Button>
          <Button variant="contained" onClick={() => setIssued(null)}>
            Done
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}

function EdgesTab() {
  const { token } = useSession();
  const { data, load, error, setError } = useLoad<Edge[]>("/admin/edges", 15_000);
  const revoke = async (id: string) => {
    const r = await http.post(token, `/admin/edges/${id}/revoke`);
    if (!r.ok) setError(r.error);
    load();
  };
  return (
    <Stack spacing={2}>
      {error && (
        <Alert severity="error" onClose={() => setError("")}>
          {error}
        </Alert>
      )}
      <Paper sx={{ overflowX: "auto" }}>
        <Table size="small" sx={{ minWidth: 980 }}>
          <TableHead>
            <TableRow>
              <TableCell>Pandia</TableCell>
              <TableCell>Site</TableCell>
              <TableCell>Host</TableCell>
              <TableCell>DICOM</TableCell>
              <TableCell>Agent</TableCell>
              <TableCell>Connection</TableCell>
              <TableCell>Instruments</TableCell>
              <TableCell>Health</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {(data ?? []).map((e) => {
              const streams = (e.lastHealth?.detail as { streams?: { name: string; ready: boolean }[] } | undefined)
                ?.streams;
              return (
                <TableRow key={e.id} sx={{ opacity: e.revokedAt ? 0.5 : 1 }}>
                  <TableCell sx={{ fontWeight: 500 }}>
                    <Link
                      component={RouterLink}
                      to={`/admin/edges/${e.id}`}
                      underline="hover"
                      color="inherit"
                      sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}
                    >
                      {e.name}
                      <ArrowRight size={12} />
                    </Link>
                  </TableCell>
                  <TableCell>
                    {e.tenant.code} / {e.site?.code ?? e.siteCode}
                  </TableCell>
                  <TableCell>
                    {e.hostname ?? "not reported"}{" "}
                    {e.arch && (
                      <Typography variant="caption" color="text.secondary">
                        {" "}
                        {e.arch}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Mono>
                      {e.edgeAeTitle}:{e.dicomPort}
                    </Mono>
                  </TableCell>
                  <TableCell>
                    {(e.lastHealth?.detail as { agentVersion?: string } | undefined)?.agentVersion ??
                      e.agentVersion ??
                      "-"}
                  </TableCell>
                  <TableCell>
                    <Tooltip
                      title={
                        e.revokedAt
                          ? "Revoked"
                          : e.online
                          ? "Online: this Pandia is checking in"
                          : "Offline: no recent check-in"
                      }
                    >
                      <span>
                        <ConnectionDots
                          state={e.revokedAt ? "idle" : e.online ? "live" : "failed"}
                          from={<HardDrives size={16} />}
                          to={<Planet size={16} />}
                          dots={5}
                          label={e.revokedAt ? "revoked" : ago(e.lastSeenAt)}
                        />
                      </span>
                    </Tooltip>
                  </TableCell>
                  <TableCell>{e._count.nodes}</TableCell>
                  <TableCell>
                    <Tooltip title={JSON.stringify(e.lastHealth?.detail ?? {}, null, 1)}>
                      <span>
                        {e.lastHealth?.status ?? "-"}
                        {streams?.length ? ` · ${streams.filter((s) => s.ready).length}/${streams.length} streams` : ""}
                      </span>
                    </Tooltip>
                  </TableCell>
                  <TableCell align="right">
                    {!e.revokedAt && (
                      <Button size="small" color="error" onClick={() => revoke(e.id)}>
                        Revoke
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
            {(data ?? []).length === 0 && (
              <TableRow>
                <TableCell colSpan={9} sx={{ py: 4, textAlign: "center", color: "text.secondary" }}>
                  No Pandia boxes yet. To add one, open the Sites & enrollment tab.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Paper>
    </Stack>
  );
}

function InstrumentsTab() {
  const { token } = useSession();
  const { data: nodes, load, error, setError } = useLoad<DicomNode[]>("/admin/nodes", 10_000);
  const { data: modules } = useLoad<ModuleRow[]>("/admin/modules");
  const { data: edges } = useLoad<Edge[]>("/admin/edges");
  const [view, setView] = useState(0);
  const [installing, setInstalling] = useState<{ node: DicomNode; initial?: Record<string, string | boolean> } | null>(
    null
  );
  const [runs, setRuns] = useState<Record<string, DiagRun>>({});
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const runsRef = useRef(runs);
  runsRef.current = runs;
  const act = async (n: DicomNode, a: "reject" | "echo") => {
    const r = await http.post(token, `/admin/nodes/${n.id}/${a}`);
    if (!r.ok) setError(r.error);
    load();
  };
  const test = async (n: DicomNode) => {
    const local = new Date().toISOString();
    setRuns((x) => ({ ...x, [n.id]: { phase: "running", requestedAt: local, started: Date.now() } }));
    setExpanded((x) => ({ ...x, [n.id]: true }));
    const r = await http.post<{ id: string; diagnoseRequestedAt: string }>(token, `/admin/nodes/${n.id}/diagnose`);
    if (!r.ok) {
      const unavailable = r.error.startsWith("404");
      setRuns((x) => ({
        ...x,
        [n.id]: { ...x[n.id], phase: unavailable ? "unavailable" : "error", message: r.error },
      }));
      return;
    }
    // Compare against the server's own clock when it tells us the request time.
    const requestedAt = r.result?.diagnoseRequestedAt ?? local;
    setRuns((x) => ({ ...x, [n.id]: { ...x[n.id], requestedAt } }));
  };

  const runningKey = Object.keys(runs)
    .filter((k) => runs[k].phase === "running")
    .sort()
    .join(",");
  useEffect(() => {
    if (!runningKey || !token) return;
    const iv = setInterval(async () => {
      const r = await http.get<DicomNode[]>(token, "/admin/nodes");
      const now = Date.now();
      let finished = false;
      const next = { ...runsRef.current };
      for (const id of runningKey.split(",")) {
        const run = next[id];
        if (!run || run.phase !== "running") continue;
        const d = r.ok ? r.result.find((x) => x.id === id)?.lastDiagnostic : undefined;
        if (newer(d, run.requestedAt)) {
          next[id] = { ...run, phase: "done", result: d! };
          finished = true;
        } else if (now - run.started > DIAG_MAX_MS) {
          next[id] = { ...run, phase: "timeout" };
          finished = true;
        }
      }
      setRuns(next);
      if (finished) load();
    }, DIAG_POLL_MS);
    return () => clearInterval(iv);
  }, [runningKey, token, load]);

  const pending = (nodes ?? []).filter((n) => n.status === "pending");
  const others = (nodes ?? []).filter((n) => n.status !== "pending");
  const diagChip = (n: DicomNode) => {
    const run = runs[n.id];
    const queued = !run && !!n.diagnoseRequestedAt && !newer(n.lastDiagnostic, n.diagnoseRequestedAt);
    if (run?.phase === "running" || queued)
      return <ConnectionDots state="connecting" dots={4} size={3} label="testing" />;
    const d = run?.result ?? n.lastDiagnostic;
    if (!d) return null;
    const failed = !d.ok;
    const warned = d.ok && d.steps.some((x) => x.status === "warn");
    return (
      <Box
        component="button"
        onClick={() => setExpanded((x) => ({ ...x, [n.id]: !x[n.id] }))}
        sx={{
          all: "unset",
          cursor: "pointer",
          display: "inline-flex",
          borderRadius: "6px",
          "&:focus-visible": { outline: `1px solid ${tones.ochre}` },
        }}
        aria-label="Show the last connection test"
      >
        <StatusChip
          status={failed ? "failed" : "ok"}
          tone={failed ? "fail" : warned ? "warn" : "ok"}
          label={`test ${failed ? "failed" : warned ? "warnings" : "ok"} · ${ago(d.at)}`}
        />
      </Box>
    );
  };
  const row = (n: DicomNode) => {
    const run = runs[n.id];
    const showPanel = !!expanded[n.id] && (!!run || !!n.lastDiagnostic);
    return (
      <Fragment key={n.id}>
        <TableRow sx={showPanel ? { "& td": { borderBottom: 0 } } : undefined}>
          <TableCell>
            <Link
              component={RouterLink}
              to={`/admin/instruments/${n.id}`}
              underline="hover"
              sx={{ display: "inline-flex", alignItems: "center", gap: 0.5, color: "primary.light" }}
            >
              <Mono>{n.aet}</Mono>
              <ArrowRight size={12} />
            </Link>
          </TableCell>
          <TableCell>
            {n.displayName ?? ""}
            {n.manufacturer && (
              <Typography variant="caption" display="block" color="text.secondary">
                {n.manufacturer} {n.model}
              </Typography>
            )}
          </TableCell>
          <TableCell>
            {n.device.tenant.code} / {n.device.siteCode}
            <Typography variant="caption" display="block" color="text.secondary">
              {n.device.name}
            </Typography>
          </TableCell>
          <TableCell>
            <Mono>
              {n.host ?? "?"}
              {n.port ? `:${n.port}` : ""}
            </Mono>
          </TableCell>
          <TableCell>
            {ago(n.lastSeenAt)}
            <Typography variant="caption" display="block" color="text.secondary">
              {n.lastSeenKind} · {n.seenCount}x
            </Typography>
          </TableCell>
          <TableCell>
            {n.modules.map((m) => (
              <Chip
                key={m.moduleId}
                size="small"
                label={m.moduleId}
                sx={{ mr: 0.5, fontFamily: fonts.mono, fontSize: "0.66rem" }}
              />
            ))}
            {!n.modules.length && n.suggestedModule && (
              <Typography variant="caption" color="text.secondary">
                suggested: {n.suggestedModule}
              </Typography>
            )}
          </TableCell>
          <TableCell>
            <Stack spacing={0.75} alignItems="flex-start">
              {n.echoRequestedAt ? (
                <ConnectionDots state="connecting" dots={4} size={3} label="echo pending" />
              ) : n.lastEcho ? (
                <Tooltip title={n.lastEcho.detail ?? ""}>
                  <span>
                    <StatusChip
                      status={n.lastEcho.ok ? "ok" : "failed"}
                      label={n.lastEcho.ok ? "echo ok" : "echo failed"}
                    />
                  </span>
                </Tooltip>
              ) : null}
              {diagChip(n)}
            </Stack>
          </TableCell>
          <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
            {n.status !== "rejected" && (
              <Button
                size="small"
                startIcon={<Pulse size={14} />}
                disabled={run?.phase === "running"}
                onClick={() => test(n)}
              >
                {run?.phase === "running" ? "Testing" : "Test connection"}
              </Button>
            )}
            {n.status !== "rejected" && (
              <Button
                size="small"
                variant={n.status === "pending" ? "contained" : "text"}
                onClick={() => setInstalling({ node: n })}
              >
                {n.status === "pending" ? "Approve" : "Module"}
              </Button>
            )}
            {n.status === "approved" &&
              (n.port ? (
                <Button size="small" onClick={() => act(n, "echo")}>
                  C-ECHO
                </Button>
              ) : (
                <Tooltip title="This instrument only initiates DICOM connections (worklist, store). It has no listener, so C-ECHO cannot test it.">
                  <Typography variant="caption" color="text.secondary" sx={{ mx: 1 }}>
                    initiates only
                  </Typography>
                </Tooltip>
              ))}
            {n.status !== "rejected" && (
              <Button size="small" color="error" onClick={() => act(n, "reject")}>
                Reject
              </Button>
            )}
          </TableCell>
        </TableRow>
        {showPanel && (
          <TableRow>
            <TableCell colSpan={8} sx={{ pt: 0, pb: 2 }}>
              <DiagnosticPanel
                aet={n.aet}
                run={run}
                last={n.lastDiagnostic ?? null}
                onHide={() => setExpanded((x) => ({ ...x, [n.id]: false }))}
              />
            </TableCell>
          </TableRow>
        )}
      </Fragment>
    );
  };
  const head = (
    <TableHead>
      <TableRow>
        <TableCell>AE title</TableCell>
        <TableCell>Instrument</TableCell>
        <TableCell>Site</TableCell>
        <TableCell>Address</TableCell>
        <TableCell>Last seen</TableCell>
        <TableCell>Modules</TableCell>
        <TableCell>Connectivity</TableCell>
        <TableCell />
      </TableRow>
    </TableHead>
  );
  return (
    <Stack spacing={2}>
      {error && (
        <Alert severity="error" onClose={() => setError("")}>
          {error}
        </Alert>
      )}
      <Tabs value={view} onChange={(_, v) => setView(v)} sx={{ minHeight: 36, "& .MuiTab-root": { minHeight: 36 } }}>
        <Tab label="Instruments" />
        <Tab label="Add by hand" icon={<Plus size={14} />} iconPosition="start" />
        <Tab label="Automatic discovery" />
      </Tabs>
      {view === 1 && modules && (
        <AddByHand
          edges={(edges ?? []).filter((e) => !e.revokedAt)}
          modules={modules}
          onCreated={async (id, moduleId, initial) => {
            const r = await http.get<DicomNode[]>(token, "/admin/nodes");
            const n = r.ok ? r.result.find((x) => x.id === id) : undefined;
            if (!n) return setError("The instrument was created but could not be loaded. Refresh the page.");
            setView(0);
            load();
            setInstalling({ node: { ...n, suggestedModule: moduleId }, initial });
          }}
        />
      )}
      {view === 2 && (
        <AutomaticDiscovery edges={(edges ?? []).filter((e) => !e.revokedAt)} onManual={() => setView(1)} />
      )}
      {view === 0 && (
        <>
          <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 900 }}>
            Instruments appear here by themselves after their first worklist query or image transfer to a Pandia (see
            Automatic discovery). An instrument that never contacts Pandia over DICOM, for example a slit lamp used only
            for live video, is added by hand. Connections from an AE title are not accepted until an administrator
            approves it. Approving installs the device module. Test connection checks the instrument from its Pandia and
            explains every failure here, no log digging needed.
          </Typography>
          <SectionLabel count={pending.length}>Pending</SectionLabel>
          <Paper sx={{ overflowX: "auto" }}>
            <Table size="small" sx={{ minWidth: 1100 }}>
              {head}
              <TableBody>
                {pending.map(row)}
                {!pending.length && (
                  <TableRow>
                    <TableCell colSpan={8} sx={{ py: 3, textAlign: "center", color: "text.secondary" }}>
                      No instruments waiting.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </Paper>
          <SectionLabel count={others.length}>Registered</SectionLabel>
          <Paper sx={{ overflowX: "auto" }}>
            <Table size="small" sx={{ minWidth: 1100 }}>
              {head}
              <TableBody>{others.map(row)}</TableBody>
            </Table>
          </Paper>
        </>
      )}
      {installing && modules && (
        <InstallDialog
          node={installing.node}
          initial={installing.initial}
          modules={modules}
          onClose={(ok) => {
            setInstalling(null);
            if (ok) load();
          }}
        />
      )}
    </Stack>
  );
}

function InstallDialog({
  node,
  initial,
  modules,
  onClose,
}: {
  node: DicomNode;
  initial?: Record<string, string | boolean>;
  modules: ModuleRow[];
  onClose: (ok: boolean) => void;
}) {
  const { token } = useSession();
  const existing = node.modules[0];
  const [moduleId, setModuleId] = useState(existing?.moduleId ?? node.suggestedModule ?? modules[0]?.id ?? "");
  const [displayName, setDisplayName] = useState(node.displayName ?? "");
  const [values, setValues] = useState<Record<string, string | number | boolean>>({
    ...initial,
    ...(existing?.settings as Record<string, string>),
  });
  const [error, setError] = useState("");
  const mod = modules.find((m) => m.id === moduleId);
  const save = async () => {
    if (displayName !== (node.displayName ?? "")) await http.patch(token, `/admin/nodes/${node.id}`, { displayName });
    const settings: Record<string, string | number | boolean> = {};
    for (const s of mod?.manifest.settings ?? []) {
      const v = values[s.key] ?? s.default;
      if (v === undefined || v === "") continue;
      settings[s.key] = s.type === "port" ? Number(v) : v;
    }
    const r = await http.post(token, `/admin/nodes/${node.id}/modules`, { moduleId, settings });
    if (r.ok) onClose(true);
    else setError(r.error);
  };
  return (
    <Dialog open onClose={() => onClose(false)} fullWidth maxWidth="sm">
      <DialogTitle>
        {node.status === "pending" ? "Approve" : "Configure"} {node.aet}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {error && <Alert severity="error">{error}</Alert>}
          <Typography variant="body2" color="text.secondary">
            Seen at {node.host ?? "unknown address"} via {node.lastSeenKind}
            {node.manufacturer ? `, reports ${node.manufacturer} ${node.model ?? ""}` : ""}.
          </Typography>
          <TextField
            label="Display name"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            placeholder="Exam room 2 slit lamp"
          />
          <TextField
            select
            label="Device module"
            value={moduleId}
            onChange={(e) => {
              setModuleId(e.target.value);
              setValues({});
            }}
          >
            {modules.map((m) => (
              <MenuItem key={m.id} value={m.id}>
                {m.name} ({m.vendor}, v{m.version})
              </MenuItem>
            ))}
          </TextField>
          {mod?.manifest.settings.map((s) =>
            s.type === "boolean" ? (
              <FormControlLabel
                key={s.key}
                control={
                  <Switch
                    checked={!!(values[s.key] ?? s.default)}
                    onChange={(e) => setValues({ ...values, [s.key]: e.target.checked })}
                  />
                }
                label={s.label}
              />
            ) : (
              <TextField
                key={s.key}
                label={`${s.label}${s.required ? " *" : ""}`}
                type={s.type === "secret" ? "password" : s.type === "port" ? "number" : "text"}
                select={!!s.options?.length}
                value={String(values[s.key] ?? (s.type === "secret" ? "" : s.default ?? ""))}
                onChange={(e) => setValues({ ...values, [s.key]: e.target.value })}
                helperText={
                  s.type === "secret" ? `${s.help ?? ""} Stored in AWS Secrets Manager, never shown again.` : s.help
                }
              >
                {s.options?.map((o) => (
                  <MenuItem key={o.value} value={o.value}>
                    {o.label}
                  </MenuItem>
                ))}
              </TextField>
            )
          )}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button color="inherit" onClick={() => onClose(false)}>
          Cancel
        </Button>
        <Button variant="contained" onClick={save} disabled={!moduleId}>
          {node.status === "pending" ? "Approve and install" : "Save"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

const IPV4 = /^(25[0-5]|2[0-4]\d|1?\d?\d)(\.(25[0-5]|2[0-4]\d|1?\d?\d)){3}$/;
const HOSTNAME = /^[A-Za-z0-9]([A-Za-z0-9-]{0,61}[A-Za-z0-9])?(\.[A-Za-z0-9]([A-Za-z0-9-]{0,61}[A-Za-z0-9])?)*$/;

function Steps({ items }: { items: React.ReactNode[] }) {
  return (
    <Box component="ol" sx={{ m: 0, pl: 2.5, "& li": { mb: 1 }, color: "text.secondary", fontSize: "0.875rem" }}>
      {items.map((x, i) => (
        <li key={i}>{x}</li>
      ))}
    </Box>
  );
}

/** Register an instrument that never contacts Pandia over DICOM (a live-video-only slit lamp), or before it does. */
function AddByHand({
  edges,
  modules,
  onCreated,
}: {
  edges: Edge[];
  modules: ModuleRow[];
  onCreated: (id: string, moduleId: string, initial: Record<string, string | boolean>) => void;
}) {
  const { token } = useSession();
  const [deviceId, setDeviceId] = useState(edges.length === 1 ? edges[0].id : "");
  const [moduleId, setModuleId] = useState(modules[0]?.id ?? "");
  const [displayName, setDisplayName] = useState("");
  const [host, setHost] = useState("");
  const [aet, setAet] = useState("");
  const [live, setLive] = useState(true);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const mod = modules.find((m) => m.id === moduleId);
  const hasLive = !!mod?.manifest.live;
  const edge = edges.find((e) => e.id === deviceId);
  const suggestedAe = (mod?.manifest.device.model ?? "INSTRUMENT")
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, "")
    .slice(0, 12);
  const ae = (aet || `${suggestedAe}-${(edge?._count.nodes ?? 0) + 1}`).toUpperCase();
  const hostBad = !!host && !IPV4.test(host.trim()) && !HOSTNAME.test(host.trim());
  const aeBad = !AE_PATTERN.test(ae);
  const missing = [
    !deviceId && "a Pandia",
    !moduleId && "an instrument type",
    !host.trim() && "the instrument's address",
  ]
    .filter(Boolean)
    .join(", ");
  const submit = async () => {
    setBusy(true);
    setError("");
    const r = await http.post<{ id: string }>(token, "/admin/nodes", {
      deviceId,
      aet: ae,
      host: host.trim(),
      displayName: displayName.trim() || undefined,
    });
    setBusy(false);
    if (!r.ok) {
      const f = fieldErrors(r.error);
      return setError(
        Object.keys(f).length
          ? Object.entries(f)
              .map(([k, v]) => `${k}: ${v}`)
              .join(". ")
          : r.error.replace(/^\d+: /, "")
      );
    }
    const initial: Record<string, string | boolean> = { lampHost: host.trim() };
    if (hasLive && live) Object.assign(initial, { liveEnabled: true, streamHost: host.trim() });
    onCreated(r.result.id, moduleId, initial);
  };
  return (
    <Grid container spacing={2}>
      <Grid item xs={12} md={5}>
        <Card>
          <CardContent>
            <Typography variant="subtitle2" gutterBottom>
              When to add by hand
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Most instruments add themselves (see Automatic discovery). Add by hand when the instrument has no DICOM
              licence or is not set up yet, for example a Haag-Streit Elara 900 used only for live video, or when you
              want it ready before the installer arrives.
            </Typography>
            <Typography variant="subtitle2" gutterBottom>
              Steps
            </Typography>
            <Steps
              items={[
                "Plug the instrument into the same network as its Pandia (wired is best) and give it a fixed IP or a DHCP reservation.",
                "Find its IP on the instrument. Elara 900: gear icon > About.",
                "Fill in the form and press Add. Jupiter registers it on that Pandia, already trusted.",
                "In the next window, approve it and fill in the module settings. For live video: switch on Live video, then enter the stream path, user and password the manufacturer gave you.",
                "Back on the Instruments list, press Test connection. Pandia tries the instrument from the clinic network and shows each step (network, certificate, login, stream path, video format, video arriving) with what to fix if a step fails.",
                "Pin the certificate fingerprint the test shows, so only this exact instrument is accepted.",
              ]}
            />
          </CardContent>
        </Card>
      </Grid>
      <Grid item xs={12} md={7}>
        <Card>
          <CardContent>
            <Stack spacing={2}>
              <Typography variant="subtitle2">Add an instrument</Typography>
              {!edges.length && (
                <Alert severity="warning">
                  No Pandia is enrolled in this practice yet. Install Pandia on a clinic computer and enroll it (Pandia
                  tab) first; instruments always belong to a Pandia on their network.
                </Alert>
              )}
              {error && (
                <Alert severity="error" onClose={() => setError("")}>
                  {error}
                </Alert>
              )}
              <TextField
                select
                label="Pandia on the instrument's network"
                value={deviceId}
                onChange={(e) => setDeviceId(e.target.value)}
                helperText={
                  edge && !edge.online
                    ? "This Pandia is offline. You can add the instrument now; it is configured when Pandia next checks in."
                    : "The Pandia that can reach the instrument on the clinic network."
                }
                FormHelperTextProps={{ sx: edge && !edge.online ? { color: tones.ochre } : undefined }}
              >
                {edges.map((e) => (
                  <MenuItem key={e.id} value={e.id}>
                    {e.name} ({e.site?.name ?? e.siteCode}) {e.online ? "· online" : "· offline"}
                  </MenuItem>
                ))}
              </TextField>
              <TextField select label="Instrument type" value={moduleId} onChange={(e) => setModuleId(e.target.value)}>
                {modules.map((m) => (
                  <MenuItem key={m.id} value={m.id}>
                    {m.name} ({m.vendor})
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                label="Display name"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                placeholder="Exam room 1 slit lamp"
                helperText="What staff see when they start a session."
              />
              <TextField
                label="Instrument IP address"
                value={host}
                onChange={(e) => setHost(e.target.value)}
                placeholder="192.168.1.50"
                error={hostBad}
                helperText={
                  hostBad
                    ? "Enter an IP like 192.168.1.50 or a hostname, without rtsps:// or a port."
                    : "On the clinic network, as Pandia sees it."
                }
              />
              <TextField
                label="AE title"
                value={aet}
                onChange={(e) => setAet(e.target.value.toUpperCase())}
                placeholder={ae}
                error={aeBad}
                helperText={
                  aeBad
                    ? "Up to 16 characters: letters, digits, dash, underscore or dot."
                    : `The instrument's DICOM name. Leave blank to use ${ae}. If the instrument later sends DICOM, type the same value in its own settings so both match.`
                }
              />
              {hasLive && (
                <FormControlLabel
                  control={<Switch checked={live} onChange={(e) => setLive(e.target.checked)} />}
                  label="Live video (remote sessions)"
                />
              )}
              <Stack direction="row" spacing={1.5} alignItems="center">
                <Button
                  variant="contained"
                  startIcon={<Plus size={14} />}
                  disabled={busy || !!missing || hostBad || aeBad}
                  onClick={submit}
                >
                  {busy ? "Adding" : "Add instrument"}
                </Button>
                {missing && (
                  <Typography variant="caption" color="text.secondary">
                    Still needed: {missing}
                  </Typography>
                )}
              </Stack>
            </Stack>
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );
}

/** What it looks like when an instrument registers itself: the instrument contacts Pandia, Jupiter lists it. */
function AutomaticDiscovery({ edges, onManual }: { edges: Edge[]; onManual: () => void }) {
  return (
    <Grid container spacing={2}>
      <Grid item xs={12} md={6}>
        <Card sx={{ height: "100%" }}>
          <CardContent>
            <Typography variant="subtitle2" gutterBottom>
              How an instrument adds itself
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              No typing in Jupiter. The installer points the instrument at Pandia once; its first DICOM contact
              registers it here.
            </Typography>
            <Steps
              items={[
                "On the instrument's network settings (Elara 900: Webmin, https://<lamp IP>:10000, technician login), enter Pandia as both the worklist server and the storage server, using the values on the right.",
                "Give the instrument its own AE title (for example ELARA01) and save. DICOM needs the instrument's DICOM licence (Elara: DICOM Connectivity, ref 1024947).",
                "On the instrument, open the patient list (Elara: patient tab, then the funnel with Today, Station and Slit lamp on) or send a test image. Any worklist query, image or C-ECHO counts.",
                "Within seconds the instrument appears under Instruments > Pending with its address, AE title and maker. Nothing is accepted from it yet.",
                "Press Approve. Jupiter suggests the right module from the maker it reported; check the settings and save.",
                "Today's appointments from the EMR now show on the instrument's patient list, and captured images land on the order.",
              ]}
            />
          </CardContent>
        </Card>
      </Grid>
      <Grid item xs={12} md={6}>
        <Stack spacing={2}>
          <Card>
            <CardContent>
              <Typography variant="subtitle2" gutterBottom>
                What to enter on the instrument
              </Typography>
              {!edges.length && (
                <Typography variant="body2" color="text.secondary">
                  Enroll a Pandia first; its values appear here.
                </Typography>
              )}
              {edges.map((e) => (
                <Box key={e.id} sx={{ mb: 1.5 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {e.name} {e.online ? "" : "(offline)"}
                  </Typography>
                  <Table size="small">
                    <TableBody>
                      <TableRow>
                        <TableCell sx={{ color: "text.secondary" }}>Worklist and storage AE title</TableCell>
                        <TableCell>
                          <Mono>{e.edgeAeTitle}</Mono>
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell sx={{ color: "text.secondary" }}>Host</TableCell>
                        <TableCell>
                          <Link component={RouterLink} to={`/admin/edges/${e.id}`} underline="hover">
                            Pandia's LAN address (on its page)
                          </Link>
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell sx={{ color: "text.secondary" }}>Port</TableCell>
                        <TableCell>
                          <Mono>{e.dicomPort}</Mono>
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </Box>
              ))}
            </CardContent>
          </Card>
          <Card>
            <CardContent>
              <Typography variant="subtitle2" gutterBottom>
                If it does not appear
              </Typography>
              <Steps
                items={[
                  "Pandia offline: the Pandia tab marks it offline. Check the clinic computer is on and connected.",
                  "Wrong host or port on the instrument: Pandia never sees it. Compare with the values above.",
                  "The instrument uses secure DICOM (TLS): turn on Pandia's TLS listener on the Pandia page and use that port instead.",
                  "Firewall on the Pandia computer: the installer opens the DICOM port for private networks only. Set the clinic network to Private.",
                  "Each Pandia page shows a live event log of every DICOM contact, accepted or refused, with the reason.",
                  <>
                    Instrument has no DICOM licence, or is live video only:{" "}
                    <Link component="button" onClick={onManual} sx={{ verticalAlign: "baseline" }}>
                      add it by hand
                    </Link>
                    .
                  </>,
                ]}
              />
            </CardContent>
          </Card>
        </Stack>
      </Grid>
    </Grid>
  );
}

function UsersTab() {
  const { token, me } = useSession();
  const { data, load, error, setError } = useLoad<UserRow[]>("/admin/users");
  const setRole = async (u: UserRow, role: string) => {
    const r = await http.patch(token, `/admin/users/${u.id}`, { role });
    if (!r.ok) setError(r.error);
    load();
  };
  return (
    <Stack spacing={2}>
      {error && (
        <Alert severity="error" onClose={() => setError("")}>
          {error}
        </Alert>
      )}
      <Typography variant="body2" color="text.secondary">
        Users appear after their first sign-in with their Moyae (Auth0) account. Roles: admin (full access), reader
        (orders, studies, live view), tech (worklist and live view), vendor (live view and device health).
      </Typography>
      <Paper sx={{ overflowX: "auto" }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Email</TableCell>
              <TableCell>Name</TableCell>
              <TableCell>Last seen</TableCell>
              <TableCell>Role</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {(data ?? []).map((u) => (
              <TableRow key={u.id}>
                <TableCell sx={{ fontWeight: 500 }}>{u.email ?? u.sub}</TableCell>
                <TableCell>{u.name}</TableCell>
                <TableCell sx={{ color: "text.secondary" }}>{ago(u.lastSeenAt)}</TableCell>
                <TableCell>
                  <TextField
                    select
                    size="small"
                    value={u.role}
                    onChange={(e) => setRole(u, e.target.value)}
                    disabled={u.id === me?.id}
                  >
                    {["admin", "reader", "tech", "vendor"].map((r) => (
                      <MenuItem key={r} value={r}>
                        {r}
                      </MenuItem>
                    ))}
                  </TextField>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>
    </Stack>
  );
}

function AuditTab() {
  const { token } = useSession();
  const [f, setF] = useState({ action: "", actor: "", target: "", from: "", to: "" });
  const [exporting, setExporting] = useState<string | null>(null);
  const query = Object.entries({ ...f, to: f.to ? `${f.to}T23:59:59.999Z` : "" })
    .filter(([, v]) => v)
    .map(([k, v]) => `${k}=${encodeURIComponent(v)}`)
    .join("&");
  const { data } = useLoad<AuditRow[]>(`/admin/audit?limit=300${query ? `&${query}` : ""}`, 15_000);
  const exportCsv = async () => {
    if (!token) return;
    setExporting("Preparing...");
    const err = await downloadFile(
      token,
      `/admin/audit?format=csv${query ? `&${query}` : ""}`,
      `jupiter-audit-${new Date().toISOString().slice(0, 10)}.csv`
    );
    setExporting(err);
  };
  const field = (k: keyof typeof f, label: string, extra?: Record<string, unknown>) => (
    <TextField
      size="small"
      label={label}
      value={f[k]}
      onChange={(e) => setF({ ...f, [k]: e.target.value })}
      sx={{ minWidth: 150 }}
      {...extra}
    />
  );
  return (
    <Stack spacing={2}>
      <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" alignItems="center">
        {field("action", "Action starts with", { placeholder: "instance. / dicom. / user." })}
        {field("actor", "Who", { placeholder: "email, account or AE title" })}
        {field("target", "What", { placeholder: "accession, MRN, image, box" })}
        {field("from", "From", { type: "date", InputLabelProps: { shrink: true } })}
        {field("to", "To", { type: "date", InputLabelProps: { shrink: true } })}
        <Button size="small" variant="outlined" onClick={exportCsv} disabled={exporting === "Preparing..."}>
          Export CSV
        </Button>
        {exporting && exporting !== "Preparing..." && (
          <Typography variant="caption" color="error">
            {exporting}
          </Typography>
        )}
      </Stack>
      <Paper sx={{ overflowX: "auto" }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>When</TableCell>
              <TableCell>Action</TableCell>
              <TableCell>Actor</TableCell>
              <TableCell>Target</TableCell>
              <TableCell>Outcome</TableCell>
              <TableCell>IP</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {(data ?? []).map((a) => (
              <TableRow key={a.id}>
                <TableCell sx={{ color: "text.secondary", whiteSpace: "nowrap" }}>
                  {new Date(a.at).toLocaleString()}
                </TableCell>
                <TableCell>
                  <Mono>{a.action}</Mono>
                </TableCell>
                <TableCell>
                  <Tooltip title={a.actorType}>
                    <span>{a.actor.length > 28 ? `${a.actor.slice(0, 28)}…` : a.actor}</span>
                  </Tooltip>
                </TableCell>
                <TableCell>{a.target}</TableCell>
                <TableCell>
                  <Tooltip title={a.detail ? JSON.stringify(a.detail) : ""}>
                    <span>
                      <StatusChip
                        status={a.outcome}
                        tone={a.outcome === "deny" || a.outcome === "error" ? "fail" : "neutral"}
                      />
                    </span>
                  </Tooltip>
                </TableCell>
                <TableCell>
                  <Tooltip title={a.userAgent ?? ""}>
                    <span>
                      <Mono sx={{ color: "text.secondary" }}>{a.ip}</Mono>
                    </span>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>
    </Stack>
  );
}

/** The site's maintenance window (local time) in which its Pandia boxes update themselves. */
function SiteWindow({ site, onSaved }: { site: Site; onSaved: () => void }) {
  const { token } = useSession();
  const [edit, setEdit] = useState<{ timezone: string; updateWindow: string } | null>(null);
  const [err, setErr] = useState("");
  const save = async () => {
    if (!token || !edit) return;
    const r = await http.patch(token, `/admin/sites/${site.id}`, edit);
    if (!r.ok) return setErr(r.error);
    setEdit(null);
    setErr("");
    onSaved();
  };
  if (!edit) {
    return (
      <Stack direction="row" spacing={1} alignItems="center">
        <Mono>{site.updateWindow ?? "02:00-05:00"}</Mono>
        <Typography variant="caption" color="text.secondary">
          {site.timezone ?? "America/New_York"}
        </Typography>
        <Button
          size="small"
          color="inherit"
          onClick={() =>
            setEdit({ timezone: site.timezone ?? "America/New_York", updateWindow: site.updateWindow ?? "02:00-05:00" })
          }
        >
          Edit
        </Button>
      </Stack>
    );
  }
  return (
    <Stack direction="row" spacing={1} alignItems="center" useFlexGap flexWrap="wrap">
      <TextField
        size="small"
        label="Window (HH:MM-HH:MM)"
        value={edit.updateWindow}
        onChange={(e) => setEdit({ ...edit, updateWindow: e.target.value })}
        sx={{ width: 170 }}
        error={!!err}
        helperText={err || undefined}
      />
      <TextField
        size="small"
        label="Time zone"
        value={edit.timezone}
        onChange={(e) => setEdit({ ...edit, timezone: e.target.value })}
        sx={{ width: 200 }}
      />
      <Button size="small" variant="contained" onClick={save}>
        Save
      </Button>
      <Button
        size="small"
        color="inherit"
        onClick={() => {
          setEdit(null);
          setErr("");
        }}
      >
        Cancel
      </Button>
    </Stack>
  );
}

/** Releases, channels and every box's update state. Ops promotes and halts; a practice sees its own boxes. */
function FleetTab() {
  const { token, me } = useSession();
  const { data, load, error, setError } = useLoad<Fleet>("/admin/fleet", 15_000);
  const [busy, setBusy] = useState(false);
  const act = async (path: string, body?: unknown) => {
    if (!token) return;
    setBusy(true);
    const r = await http.post(token, path, body ?? {});
    setBusy(false);
    if (!r.ok) setError(r.error);
    else load();
  };
  const chip = (s: string | null) =>
    s ? (
      <StatusChip status={s} tone={s === "ok" ? "ok" : s === "updating" ? "warn" : "fail"} />
    ) : (
      <Typography variant="caption" color="text.secondary">
        never
      </Typography>
    );
  return (
    <Stack spacing={2}>
      {error && (
        <Alert severity="error" onClose={() => setError("")}>
          {error}
        </Alert>
      )}
      <SectionLabel>Channels</SectionLabel>
      <Grid container spacing={2}>
        {CHANNELS.map((c) => {
          const ch = data?.channels.find((x) => x.channel === c);
          const boxes = (data?.devices ?? []).filter((d) => d.channel === c);
          const onIt = boxes.filter((d) => ch && d.release === ch.release.version).length;
          return (
            <Grid item xs={12} md={4} key={c}>
              <Paper sx={{ p: 2, borderColor: ch?.haltedAt ? alpha(tones.warn, 0.5) : undefined, height: "100%" }}>
                <Stack spacing={1}>
                  <Stack direction="row" alignItems="center" justifyContent="space-between">
                    <Typography sx={{ fontFamily: fonts.display, letterSpacing: "0.12em", textTransform: "uppercase" }}>
                      {c}
                    </Typography>
                    {ch?.haltedAt ? (
                      <Chip size="small" color="warning" label="halted" />
                    ) : ch ? (
                      <Chip size="small" label={`stage ${ch.stage + 1} of 3: ${ch.stageLabel}`} />
                    ) : (
                      <Chip size="small" label="no release" />
                    )}
                  </Stack>
                  <Typography variant="body2">
                    {ch ? (
                      <Mono>{ch.release.version}</Mono>
                    ) : (
                      <span style={{ opacity: 0.6 }}>nothing promoted here yet</span>
                    )}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {boxes.length} box{boxes.length === 1 ? "" : "es"} on this channel, {onIt} on this release
                    {ch?.haltReason ? `. Halted: ${ch.haltReason}` : ""}
                  </Typography>
                  {me?.isOps && ch && (
                    <Stack direction="row" spacing={1}>
                      {ch.haltedAt ? (
                        <Button
                          size="small"
                          variant="outlined"
                          disabled={busy}
                          onClick={() => act(`/admin/channels/${c}/resume`)}
                        >
                          Resume rollout
                        </Button>
                      ) : (
                        <Button
                          size="small"
                          color="inherit"
                          disabled={busy}
                          onClick={() => act(`/admin/channels/${c}/halt`, { reason: "halted by ops" })}
                        >
                          Halt
                        </Button>
                      )}
                    </Stack>
                  )}
                </Stack>
              </Paper>
            </Grid>
          );
        })}
      </Grid>
      {me?.isOps && (data?.releases.length ?? 0) > 0 && (
        <>
          <SectionLabel>Releases</SectionLabel>
          <Paper sx={{ overflowX: "auto" }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Version</TableCell>
                  <TableCell>Registered</TableCell>
                  <TableCell>Notes</TableCell>
                  <TableCell>On channels</TableCell>
                  <TableCell align="right">Promote to</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data!.releases.map((r) => (
                  <TableRow key={r.id}>
                    <TableCell>
                      <Mono>{r.version}</Mono>
                      {r.emergency && <Chip size="small" color="error" label="emergency" sx={{ ml: 1 }} />}
                    </TableCell>
                    <TableCell sx={{ whiteSpace: "nowrap", color: "text.secondary" }}>
                      {new Date(r.createdAt).toLocaleString()}
                    </TableCell>
                    <TableCell sx={{ maxWidth: 360 }}>{r.notes}</TableCell>
                    <TableCell>
                      {data!.channels
                        .filter((c) => c.release.id === r.id)
                        .map((c) => c.channel)
                        .join(", ") || "-"}
                    </TableCell>
                    <TableCell align="right">
                      {CHANNELS.map((c) => (
                        <Button
                          key={c}
                          size="small"
                          color="inherit"
                          disabled={busy || data!.channels.some((x) => x.channel === c && x.release.id === r.id)}
                          onClick={() => act(`/admin/releases/${r.id}/promote`, { channel: c })}
                        >
                          {c}
                        </Button>
                      ))}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Paper>
        </>
      )}
      <SectionLabel>Boxes</SectionLabel>
      <Paper sx={{ overflowX: "auto" }}>
        <Table size="small" sx={{ minWidth: 900 }}>
          <TableHead>
            <TableRow>
              <TableCell>Pandia</TableCell>
              <TableCell>Practice / site</TableCell>
              <TableCell>Channel</TableCell>
              <TableCell>Running</TableCell>
              <TableCell>Last update</TableCell>
              <TableCell>Window</TableCell>
              <TableCell>Notes</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {(data?.devices ?? []).map((d) => (
              <TableRow key={d.id} sx={{ bgcolor: d.stuck ? alpha(tones.warn, 0.06) : undefined }}>
                <TableCell>
                  <Link component={RouterLink} to={`/admin/edges/${d.id}`} underline="hover" color="inherit">
                    {d.name}
                  </Link>
                  {!d.online && (
                    <Typography variant="caption" color="text.secondary">
                      {" "}
                      offline
                    </Typography>
                  )}
                </TableCell>
                <TableCell>
                  {d.tenant} / {d.site}
                </TableCell>
                <TableCell>
                  {d.channel}
                  {d.channelOverride && (
                    <Typography variant="caption" color="text.secondary">
                      {" "}
                      (box)
                    </Typography>
                  )}
                </TableCell>
                <TableCell>
                  <Mono>{d.release ?? "-"}</Mono>
                </TableCell>
                <TableCell>
                  {chip(d.updateStatus)}
                  {d.target && d.updateStatus !== "ok" && (
                    <Typography variant="caption" color="text.secondary">
                      {" "}
                      to {d.target}
                    </Typography>
                  )}
                </TableCell>
                <TableCell sx={{ whiteSpace: "nowrap" }}>
                  <Mono>{d.window}</Mono>
                </TableCell>
                <TableCell sx={{ color: "text.secondary", fontSize: 12 }}>
                  {d.stuck && "No report back yet. "}
                  {d.updateDetail}
                  {d.updatesPausedUntil && new Date(d.updatesPausedUntil) > new Date()
                    ? ` Paused until ${new Date(d.updatesPausedUntil).toLocaleDateString()}${
                        d.updatesPausedReason ? `: ${d.updatesPausedReason}` : ""
                      }.`
                    : ""}
                  {d.updateNowAt && d.updateStatus !== "updating" ? " Update requested." : ""}
                </TableCell>
              </TableRow>
            ))}
            {(data?.devices ?? []).length === 0 && (
              <TableRow>
                <TableCell colSpan={7} sx={{ py: 4, textAlign: "center", color: "text.secondary" }}>
                  No boxes yet.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Paper>
    </Stack>
  );
}

/** The practice's EMR: a connect code for its admin, the subscription status, the instrument its appointments go to. */
function EmrCard() {
  const { token, me } = useSession();
  type Emr = {
    defaultStationAe: string | null;
    pendingCode: { hint: string; expiresAt: string } | null;
    connection: {
      emr: string;
      practiceName: string | null;
      fhirBaseUrl: string;
      status: string;
      subscribed: boolean;
      lastSyncAt: string | null;
      lastEventAt: string | null;
      lastError: string | null;
      since: string;
    } | null;
  };
  const { data, load, error, setError } = useLoad<Emr>("/admin/emr", 15_000);
  const [code, setCode] = useState<{ code: string; expiresAt: string } | null>(null);
  const [ae, setAe] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const { data: nodes } = useLoad<DicomNode[]>("/admin/nodes");
  const approved = (nodes ?? []).filter((n) => n.status === "approved");
  const act = async (fn: () => Promise<{ ok: boolean; error?: string } & Record<string, unknown>>) => {
    setBusy(true);
    setMsg("");
    const r = await fn();
    setBusy(false);
    if (!r.ok) setError(r.error ?? "failed");
    else load();
    return r;
  };
  const c = data?.connection;
  return (
    <Paper sx={{ p: 2 }}>
      <Stack spacing={1.5}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" useFlexGap flexWrap="wrap">
          <Typography variant="overline" color="text.secondary">
            Connect an EMR
          </Typography>
          {c ? (
            <StatusChip
              status={c.status === "connected" ? "ok" : "fail"}
              label={`${c.emr}${c.practiceName ? `: ${c.practiceName}` : ""}, ${
                c.subscribed ? "subscribed" : "no subscription"
              }`}
            />
          ) : (
            <Chip size="small" label="not connected" />
          )}
        </Stack>
        {error && (
          <Alert severity="error" onClose={() => setError("")}>
            {error}
          </Alert>
        )}
        <Typography variant="body2" color="text.secondary">
          The EMR sends every booked, arrived, changed or cancelled appointment the moment it happens (a FHIR
          Subscription), and Jupiter turns it into a worklist entry for the instrument below. Generate a code, then in
          the EMR's practice settings choose Connect to Jupiter and paste it. Nothing else to configure on either side.
        </Typography>
        <Stack direction="row" spacing={1} alignItems="center" useFlexGap flexWrap="wrap">
          <Button
            size="small"
            variant={c ? "outlined" : "contained"}
            disabled={busy}
            onClick={() =>
              act(async () => {
                const r = await http.post<{ code: string; expiresAt: string }>(token!, "/admin/emr/connect-code");
                if (r.ok) setCode(r.result);
                return r;
              })
            }
          >
            {c ? "New connect code (reconnect)" : "Generate connect code"}
          </Button>
          {code && new Date(code.expiresAt) > new Date() && (
            <Typography variant="body2">
              Code <Mono sx={{ fontSize: 18, letterSpacing: "0.12em" }}>{code.code}</Mono>{" "}
              <Typography component="span" variant="caption" color="text.secondary">
                (valid 60 minutes, one use)
              </Typography>
            </Typography>
          )}
          {!code && data?.pendingCode && (
            <Typography variant="caption" color="text.secondary">
              A code ending in {data.pendingCode.hint} is waiting to be used.
            </Typography>
          )}
        </Stack>
        <Stack direction="row" spacing={1} alignItems="center" useFlexGap flexWrap="wrap">
          <TextField
            select
            size="small"
            label="Appointments go to"
            value={ae ?? data?.defaultStationAe ?? ""}
            sx={{ minWidth: 260 }}
            disabled={busy}
            onChange={(e) => {
              setAe(e.target.value);
              act(() => http.patch(token!, "/admin/emr", { defaultStationAe: e.target.value || null }));
            }}
            helperText="The instrument whose worklist shows the EMR's appointments"
          >
            <MenuItem value="">(none: appointments are not imported)</MenuItem>
            {approved.map((n) => (
              <MenuItem key={n.id} value={n.aet}>
                {n.displayName ?? n.aet} ({n.aet})
              </MenuItem>
            ))}
          </TextField>
          {c && (
            <Button
              size="small"
              color="inherit"
              disabled={busy}
              onClick={() =>
                act(async () => {
                  const r = await http.post<{ seen: number; created: number; updated: number }>(
                    token!,
                    "/admin/emr/sync"
                  );
                  if (r.ok)
                    setMsg(`${r.result.seen} appointments seen, ${r.result.created} new, ${r.result.updated} updated.`);
                  return r;
                })
              }
            >
              Refresh now
            </Button>
          )}
        </Stack>
        {c && (
          <Typography variant="caption" color="text.secondary">
            Connected {ago(c.since)}. Last event {c.lastEventAt ? ago(c.lastEventAt) : "none yet"}, last refresh{" "}
            {c.lastSyncAt ? ago(c.lastSyncAt) : "none yet"}.{c.lastError ? ` Last error: ${c.lastError}` : ""} {msg}
          </Typography>
        )}
      </Stack>
    </Paper>
  );
}
